import React, { memo, useLayoutEffect, useRef } from 'react';
import type { TrackReference, TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { FaThumbtack, FaThumbtackSlash } from 'react-icons/fa6';
import { ParticipantVideoTile } from './ParticipantVideoTile.js';
import { displayNameOf, tileKey } from './track-references.js';

interface FullScreenTileProps {
  /** The camera or screen-share reference for this tile */
  trackRef: TrackReferenceOrPlaceholder;

  /** Subscribed microphone per participant identity, for the speaking ring */
  microphoneTrackRefs: ReadonlyMap<string, TrackReference>;

  /** The pinned tile: its button always shows, as "Unpin" */
  isPinned?: boolean;

  /** Pin this tile by its `tileKey`. Without it (and `onUnpin`) there is no pin button. */
  onPin?: (key: string) => void;

  /** Unpin this tile, the pinned one */
  onUnpin?: (key: string) => void;

  /**
   * Holds the `tileKey` whose pin button should take focus when it mounts:
   * pinning or unpinning swaps the layout, which remounts the button, and
   * keyboard focus follows it rather than falling back to the page.
   */
  focusRequestRef?: React.MutableRefObject<string | null>;
}

/**
 * One tile of the full-screen layouts (issue #124): the same
 * {@link ParticipantVideoTile} as the corner tiles, with a pin button.
 *
 * The button is a real `<button>` labelled "Pin <name>" (or "Pin <name>'s
 * screen") with `aria-pressed`. On desktop it shows on hover and on keyboard
 * focus; where there is no hover (`(hover: none)`, touch screens) it is
 * always visible and a little larger. The pinned tile always shows it, as
 * "Unpin" (icon only below 480px of overlay width).
 *
 * The corner tiles have no pin: a pin only means something in full screen.
 */
const FullScreenTileComponent: React.FC<FullScreenTileProps> = ({
  trackRef,
  microphoneTrackRefs,
  isPinned = false,
  onPin,
  onUnpin,
  focusRequestRef
}) => {
  const key = tileKey(trackRef);
  const isScreenShare = trackRef.source === Track.Source.ScreenShare;
  const name = displayNameOf(trackRef);
  const subject = isScreenShare ? `${name}'s screen` : name;
  const label = `${isPinned ? 'Unpin' : 'Pin'} ${subject}`;
  const onToggle = isPinned ? onUnpin : onPin;

  const buttonRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    if (!focusRequestRef || focusRequestRef.current !== key) return;
    focusRequestRef.current = null;
    buttonRef.current?.focus();
  }, [focusRequestRef, key]);

  return (
    <div className="group/tile relative">
      <ParticipantVideoTile
        trackRef={trackRef}
        microphoneTrackRef={isScreenShare ? undefined : microphoneTrackRefs.get(trackRef.participant.identity)}
      />

      {onToggle && (
        <button
          ref={buttonRef}
          type="button"
          data-pin-key={key}
          aria-pressed={isPinned}
          aria-label={label}
          title={label}
          onClick={() => onToggle(key)}
          className={`absolute z-20 inline-flex h-9 min-w-9 items-center justify-center gap-2 rounded-lg border bg-game-navy/80 text-[13px] font-semibold text-gray-100 shadow-md transition duration-150 motion-reduce:transition-none hover:bg-[var(--lk-bg3)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-game-golden focus-visible:ring-offset-2 focus-visible:ring-offset-game-navy [@media(hover:none)]:h-10 [@media(hover:none)]:min-w-10 ${
            isPinned
              ? 'right-3.5 top-3.5 border-game-golden @min-[480px]:px-3'
              : 'right-2 top-2 border-game-medium/70 opacity-0 group-hover/tile:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100'
          }`}
        >
          {isPinned ? (
            <>
              <FaThumbtackSlash aria-hidden="true" className="text-sm text-game-golden" />
              <span className="hidden @min-[480px]:inline">Unpin</span>
            </>
          ) : (
            <FaThumbtack aria-hidden="true" className="text-sm" />
          )}
        </button>
      )}
    </div>
  );
};

export const FullScreenTile = memo(FullScreenTileComponent);
FullScreenTile.displayName = 'FullScreenTile';
